import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { usePortalRole, ROLE_PRIORITY } from '../../contexts/PortalRoleContext';
import { getMyProfile } from '../../api/userProfileApi';
import { listCampuses } from '../../api/campusPortalApi';
import { hasMaterialAccessPage } from '../../utils/materialAccessCampus';
// import { getRoleDisplayName } from '../../utils/portalPermissions';
import * as S from './PortalNavbar.styles';

const PortalNavbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  const { currentRole, currentUserCampus, setRoles } = usePortalRole();
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRefs = useRef({});
  const [hasBetaFeatures, setHasBetaFeatures] = useState(false);
  // currentUserCampus 存的是 campusId(UUID)，标题栏需要显示可读的校区名
  const [campusName, setCampusName] = useState(null);
  // Material Access is switched on per campus (CampusPortal.materialAccess)
  const [campusMaterialAccess, setCampusMaterialAccess] = useState(false);

  // The roles this account actually holds. This is the source the whole navbar reads, and
  // what it publishes into the role context below, so the two cannot disagree.
  const userGroups = auth.user?.profile['cognito:groups'] || [];

  // Check if user's campus has beta features enabled, and whether any of their campuses has
  // the Material Access page switched on
  useEffect(() => {
    const checkBetaFeatures = async () => {
      try {
        const profile = await getMyProfile();
        const userCampusIds = profile?.campusIds?.length
          ? profile.campusIds
          : (profile?.campusId ? [profile.campusId] : []);
        const userCampusId = profile?.campusId || (userCampusIds.length ? userCampusIds[0] : null);
        if (userCampusIds.length) {
          const campuses = await listCampuses();
          const userCampus = campuses.find(c => c.id === userCampusId);
          setHasBetaFeatures(userCampus?.betaFeatures === true);
          setCampusName(userCampus?.name || null);
          setCampusMaterialAccess(hasMaterialAccessPage(campuses, { userCampusIds }));
        }
      } catch (err) {
        console.error('Error checking beta features:', err);
      }
    };
    if (auth.isAuthenticated) {
      checkBetaFeatures();
    }
  }, [auth.isAuthenticated]);

  /**
   * Publish every portal role this person holds into the role context.
   *
   * Holding more than one is normal and goes in every direction: a manager, admin, director or
   * system admin who also teaches, a teacher whose own child attends. Until 2026-09-12 this
   * pushed only the single most senior role, so the context could answer "are you a teacher?"
   * with no for somebody who teaches — while this same navbar drew a Teacher tab for them,
   * because it reads the Cognito groups directly. Two sources of truth, and the pages that
   * trusted the context bounced people off their own profiles.
   *
   * `currentRole` is still the most senior of them, so everything keyed off seniority is
   * unchanged; what is new is that the junior roles are no longer thrown away.
   */
  // What was last published, so a campus lookup that fails is not retried forever. Without it
  // the effect re-runs on its own output: setRoles replaces userRoles with a new array, and a
  // still-missing campus would send it round again.
  const publishedRoles = useRef('');

  useEffect(() => {
    if (!auth.user?.profile) return;

    const groups = auth.user.profile['cognito:groups'] || [];
    const roles = ROLE_PRIORITY.filter(role => groups.includes(`portal_${role}`));
    if (!roles.length) return;

    const signature = roles.join(',');
    if (signature === publishedRoles.current) return;
    publishedRoles.current = signature;

    // Only these roles are campus-restricted; developer and system_admin see everything.
    const needsCampus = roles.some(role => ['director', 'manager', 'admin'].includes(role));
    if (needsCampus) {
      getMyProfile().then(profile => {
        const campusIds = profile?.campusIds || (profile?.campusId ? [profile.campusId] : []);
        setRoles(roles, null, campusIds.length ? campusIds[0] : null, campusIds);
      }).catch(() => setRoles(roles));
    } else {
      setRoles(roles);
    }
  }, [auth.user, setRoles]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!Object.values(dropdownRefs.current).some(ref => ref?.contains(event.target))) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 路由变化时收起移动端抽屉与所有下拉
  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  const toggleDropdown = (name) => {
    setOpenDropdown(openDropdown === name ? null : name);
  };

  // Multi-role navigation: Build navigation based on ALL user roles
  const getNavigationStructure = () => {
    const structure = [
      {
        type: 'link',
        path: '/portal',
        label: 'Portal'
      }
    ];

    // Helper to check if user has a specific role
    const hasRole = (role) => userGroups.includes(`portal_${role}`);

    // Check for any admin-level role
    const isAdmin = hasRole('developer') || hasRole('system_admin') || hasRole('director') ||
                    hasRole('manager') || hasRole('admin');

    // Admin section (if user has any admin role)
    if (isAdmin) {
      const adminItems = [];

      // User Management - for high-level admins and admin
      if (hasRole('developer') || hasRole('system_admin') || hasRole('director') || hasRole('manager') || hasRole('admin')) {
        adminItems.push({ path: '/portal/users', label: 'Users' });
      }

      // Teacher Availability - same audience as Users: where admins/managers go to see who
      // is free before assigning a class.
      if (hasRole('developer') || hasRole('system_admin') || hasRole('director') || hasRole('manager') || hasRole('admin')) {
        adminItems.push({ path: '/portal/teacher-availability', label: 'Teacher Availability' });
      }

      // Students - all admin roles
      adminItems.push({ path: '/portal/students', label: 'Students' });

      // Campus Management - only director+
      if (hasRole('developer') || hasRole('system_admin') || hasRole('director')) {
        adminItems.push({ path: '/portal/campuses', label: 'Campuses' });
      }

      // Course Groups - admin view
      adminItems.push({ path: '/portal/course-groups', label: 'Course Groups' });

      // The campus timetable belongs next to Course Groups: it is the other half of the same
      // job, and a class can be created and edited from either. Open to every campus since
      // 2026-09-12; the trial gate it used to sit behind is gone.
      adminItems.push({ path: '/portal/timetable', label: 'Timetable' });

      // Materials
      adminItems.push({ path: '/portal/materials', label: 'Materials' });

      // Material Access audit - admin and above (the block switch inside is
      // director/manager only, enforced by the page and the backend). Only for campuses
      // with materialAccess switched on; system admins and developers always see it.
      if (hasRole('developer') || hasRole('system_admin') || campusMaterialAccess) {
        adminItems.push({ path: '/portal/material-access', label: 'Material Access' });
      }

      // Site Settings - only director and system_admin
      if (hasRole('developer') || hasRole('system_admin') || hasRole('director')) {
        adminItems.push({ path: '/portal/site-settings', label: 'Site Settings' });
      }

      // AI Chat (internal testing) - only developer and system_admin
      if (hasRole('developer') || hasRole('system_admin')) {
        adminItems.push({ path: '/portal/ai-chat', label: 'AI Chat (Beta)' });
      }

      structure.push({
        type: 'dropdown',
        label: 'Admin',
        name: 'admin',
        items: adminItems
      });
    }

    // Teacher section
    if (hasRole('teacher')) {
      const teacherItems = [
        { path: '/portal/my-course-groups', label: 'My Classes' }
      ];

      // Only their own week. The campus-wide grid lives under Admin and is not offered here:
      // it carries every class, every room booking and every class roll at the campus.
      teacherItems.push({ path: '/portal/campusTimetableTeacher', label: 'My Timetable' });

      teacherItems.push(
        { path: '/portal/teacher-dashboard', label: 'Dashboard' },
        { path: '/portal/teacher-profile', label: 'Profile' }
      );

      structure.push({
        type: 'dropdown',
        label: 'Teacher',
        name: 'teacher',
        items: teacherItems
      });
    }

    // Parent section
    if (hasRole('parent')) {
      structure.push({
        type: 'dropdown',
        label: 'Parent',
        name: 'parent',
        items: [
          { path: '/portal/my-children-classes', label: "Children's Classes" },
          { path: '/portal/parent-profile', label: 'Profile' }
        ]
      });
    }

    // Assessment section - visible to admin + teacher
    if (isAdmin || hasRole('teacher')) {
      const assessmentItems = [
        { path: '/portal/test-management', label: 'Test Management' }
      ];
      // Score transformation & schedule management - director/manager+ only
      if (hasRole('developer') || hasRole('system_admin') || hasRole('director') || hasRole('manager')) {
        assessmentItems.push(
          { path: '/portal/mock-test-transformation', label: 'Score Transformation' },
          { path: '/portal/mock-test-transformation-traditional', label: 'Score Transformation (Traditional)' }
          // { path: '/portal/assessment-schedules', label: 'Assessment Schedules' }
        );
      }
      if (assessmentItems.length === 1) {
        // Teacher only sees test management - show as simple link
        structure.push({
          type: 'link',
          path: '/portal/test-management',
          label: 'Test'
        });
      } else {
        structure.push({
          type: 'dropdown',
          label: 'Test',
          name: 'assessment',
          items: assessmentItems
        });
      }
    }

    if (isAdmin || hasRole('teacher')) {
      structure.push({
        type: 'link',
        path: '/portal/teaching-tips',
        label: 'Teaching Tips'
      });
    }

    // Billing - only high-level admins
    if (hasRole('developer') || hasRole('system_admin') || hasRole('director') || hasRole('manager')) {
      structure.push({
        type: 'link',
        path: '/portal/billing',
        label: 'Billing'
      });
    }

    // Beta Features dropdown - only for campuses with betaFeatures enabled
    if (hasBetaFeatures) {
      const betaItems = [];

      // The timetables themselves now live under Admin and Teacher, where the people who use
      // them look. What is left here is the documentation for them.

      // How to use the two timetables, plus a live check of the demo data behind them
      if (isAdmin || hasRole('teacher')) {
        betaItems.push({ path: '/portal/timetable-demo', label: 'Timetable Guide' });
      }

      if (betaItems.length > 0) {
        structure.push({
          type: 'dropdown',
          label: 'Beta Features',
          name: 'beta',
          items: betaItems
        });
      }
    }

    // My Profile - context-aware based on highest role
    if (isAdmin) {
      structure.push({
        type: 'link',
        path: '/portal/admin-profile',
        label: 'My Profile'
      });
    } else if (hasRole('teacher') && !hasRole('parent')) {
      // Teacher-only (no parent role) - profile already in Teacher dropdown
    } else if (hasRole('parent') && !hasRole('teacher')) {
      // Parent-only (no teacher role) - profile already in Parent dropdown
    }

    return structure;
  };

  const isDropdownActive = (dropdown) => {
    return dropdown.items.some(item => location.pathname === item.path);
  };

  // const handleRoleChange = (newRole) => {
  //   // For manager and admin, set appropriate campus
  //   const campus = (newRole === 'manager' || newRole === 'admin') ? 'Claremont' : null;
  //   switchRole(newRole, null, campus);
  // };

  const handleRefresh = async () => {
    try {
      // Try silent sign-in to refresh token and user info
      await auth.signinSilent();
      // Reload page to reflect updated permissions
      window.location.reload();
    } catch (error) {
      console.error('Failed to refresh:', error);
      // Fallback to page reload
      window.location.reload();
    }
  };

  const handleLogout = () => {
    const isLocalhost = window.location.hostname.includes('localhost');
    const baseUrl = isLocalhost ? 'http://localhost:3000' : `${window.location.protocol}//${window.location.hostname}`;
    const logoutUri = `${baseUrl}/login`;

    auth.removeUser();
    const clientId = "2c6na652k2lkcsaa1ol8m5chp4";
    const cognitoDomain = "https://ap-southeast-23n9j6gdzl.auth.ap-southeast-2.amazoncognito.com";
    window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
  };

  return (
    <S.NavbarContainer>
      <S.NavbarContent>
        <S.Logo>
          <Link to="/portal">
            Uplus Portal
            {(currentRole === 'director' || currentRole === 'manager' || currentRole === 'admin') && currentUserCampus && campusName && (
              <span style={{ fontSize: '12px', opacity: 0.8, marginLeft: '8px', whiteSpace: 'nowrap' }}>
                ({campusName})
              </span>
            )}
          </Link>
        </S.Logo>

        <S.NavLinks $mobileOpen={mobileOpen}>
          {getNavigationStructure().map((item) => {
            if (item.type === 'link') {
              return (
                <S.NavLink
                  key={item.path}
                  to={item.path}
                  $isActive={location.pathname === item.path}
                  onClick={() => setMobileOpen(false)}
                >
                  {item.label}
                </S.NavLink>
              );
            } else if (item.type === 'dropdown') {
              const isActive = isDropdownActive(item);
              return (
                <S.DropdownContainer
                  key={item.name}
                  ref={el => dropdownRefs.current[item.name] = el}
                >
                  <S.DropdownButton
                    $isActive={isActive}
                    $isOpen={openDropdown === item.name}
                    onClick={() => toggleDropdown(item.name)}
                  >
                    {item.label}
                  </S.DropdownButton>
                  <S.DropdownMenu $isOpen={openDropdown === item.name}>
                    {item.items.map((subItem) => (
                      <S.DropdownLink
                        key={subItem.path}
                        to={subItem.path}
                        className={location.pathname === subItem.path ? 'active' : ''}
                        onClick={() => { setOpenDropdown(null); setMobileOpen(false); }}
                      >
                        {subItem.label}
                      </S.DropdownLink>
                    ))}
                  </S.DropdownMenu>
                </S.DropdownContainer>
              );
            }
            return null;
          })}
        </S.NavLinks>

        {/* 未来可以使用 - Role switcher only visible to system admins
        {isSystemAdmin && (
          <S.RoleSwitcher>
            <S.RoleLabel>Role:</S.RoleLabel>
            <S.RoleSelect
              value={currentRole}
              onChange={(e) => handleRoleChange(e.target.value)}
            >
              <option value="system_admin">System Admin</option>
              <option value="director">Director</option>
              <option value="manager">Manager</option>
              <option value="admin">Admin</option>
              <option value="teacher">Teacher</option>
              <option value="parent">Parent</option>
            </S.RoleSelect>
            <S.RoleBadge $role={currentRole}>
              {getRoleDisplayName(currentRole)}
            </S.RoleBadge>
            <S.SettingsIcon to="/portal/settings" title="Settings">
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
              </svg>
            </S.SettingsIcon>
          </S.RoleSwitcher>
        )}
        */}

        {/* User actions: Notifications, Refresh and Logout */}
        <S.UserActions>
          <S.NotificationButton
            onClick={() => navigate('/portal/notifications')}
            title="Notifications"
            style={{
              background: location.pathname === '/portal/notifications' ? '#F1AF59' : undefined,
              color: location.pathname === '/portal/notifications' ? 'white' : undefined
            }}
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </S.NotificationButton>
          <S.RefreshButton onClick={handleRefresh} title="Refresh permissions">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </S.RefreshButton>
          <S.ActionButton $variant="logout" onClick={handleLogout} title="Logout">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </S.ActionButton>
        </S.UserActions>

        {/* 移动端汉堡按钮：切换导航抽屉 */}
        <S.Hamburger
          onClick={() => setMobileOpen(prev => !prev)}
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </S.Hamburger>
      </S.NavbarContent>
    </S.NavbarContainer>
  );
};

export default PortalNavbar;
